// Multi-tab coordination via BroadcastChannel.
//
// When the same writer opens the same script in two tabs, both tabs
// can autosave under each other and silently overwrite work. Strategy
// (last-write-wins + banner, per the M5 plan):
//
//   - Every save broadcasts { type: 'saved', scriptId, sha, ts } on
//     the lastslate-sync channel.
//   - Each tab tracks the last sha it saved itself. If it receives a
//     message about its own scriptId with a different sha, that means
//     another tab beat it to a save — show a banner asking the user
//     to reload.
//   - Real-time CRDT is out of scope; this is the "you and yourself
//     are fighting" guard, not collaborative editing.
//
// Hooked into the bridge via install* + the file.js save path
// pushing notifications through.

import { getActiveScriptId } from './active-script.js';

const CHANNEL_NAME = 'lastslate-sync';

let channel = null;
let lastLocalSha = '';
let banner = null;

function ensureChannel() {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.addEventListener('message', (ev) => {
    const msg = ev.data;
    if (!msg || msg.type !== 'saved') return;
    const active = getActiveScriptId();
    if (msg.scriptId !== active) return;
    if (msg.sha === lastLocalSha) return; // our own echo (best-effort)
    showStaleBanner();
  });
  return channel;
}

function showStaleBanner() {
  if (banner) return;
  banner = document.createElement('div');
  banner.className = 'multi-tab-banner';
  banner.setAttribute('role', 'status');
  banner.innerHTML = `
    <span class="multi-tab-banner-text">
      Another tab saved newer changes to this script.
    </span>
    <button type="button" class="btn btn-primary btn-small" data-action="reload">Reload</button>
    <button type="button" class="btn btn-secondary btn-small" data-action="dismiss">Dismiss</button>
  `;
  banner.addEventListener('click', (ev) => {
    const action = ev.target?.dataset?.action;
    if (action === 'reload') window.location.reload();
    if (action === 'dismiss') {
      banner.remove();
      banner = null;
    }
  });
  document.body.appendChild(banner);
}

export function installMultiTab() {
  ensureChannel();
}

export function announceLocalSave(sha) {
  lastLocalSha = sha || '';
  const ch = ensureChannel();
  if (!ch) return;
  const active = getActiveScriptId();
  if (!active) return;
  ch.postMessage({ type: 'saved', scriptId: active, sha, ts: Date.now() });
}
